import { FormGroup, Label, MenuToggle, MenuToggleElement, Select, SelectOption } from '@patternfly/react-core';
import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import './interpreted-type-dropdown.css';

interface InterpretedTypeDropdownProps {
    onSelect: (interpretedType: string) => void,
    value: string,
    allowEmptyValue?: boolean
}

const supportedInterpretedTypes = [
    'ORGAN',
    'EXTERNAL',
    'PTV',
    'CTV',
    'GTV',
    'CONTROL',
    'BOLUS',
    'AVOIDANCE',
    'MARKER',
    'CAVITY',
    'SUPPORT',
    'FIXATION',
    'ISOCENTER',
    'CONTRAST_AGENT',
    'TREATED_VOLUME',
    'IRRAD_VOLUME',
    'BRACHY_CHANNEL',
    'BRACHY_ACCESSORY',
    'BRACHY_SRC_APP',
    'BRACHY_CHNL_SHLD',
    'DOSE_REGION',
    'DOSE_MEASUREMENT',
];

export const EMPTY_VALUE = '';

const poiTypes = new Set(['MARKER', 'ISOCENTER',]);

const InterpretedTypeDropdown = (props: InterpretedTypeDropdownProps) => {
    const { value, onSelect, allowEmptyValue } = props;
    const { t } = useTranslation();

    const [isOpen, setIsOpen] = React.useState(false);

    const handleToggleInterpretedType = useCallback(() => {
        setIsOpen(!isOpen);
    }, [isOpen]);

    const handleSelect = useCallback((_event: unknown, value: string | number | undefined) => {
        setIsOpen(false);
        if (value !== undefined) {
            onSelect(String(value));
        }
    }, [onSelect]);

    const dropdownItems = useMemo(() => (allowEmptyValue ? [EMPTY_VALUE].concat(supportedInterpretedTypes) : supportedInterpretedTypes)
        .map(i => (
            <SelectOption key={i} value={i} className="interpreted-type-option">
                <span className="interpreted-type-option-content">
                    {i}
                    {i !== EMPTY_VALUE && poiTypes.has(i) && (
                        <Label isCompact className="interpreted-type-label">
                            {t('column.interpretedType.poi')}
                        </Label>
                    )}
                </span>
            </SelectOption>
        )),
        [allowEmptyValue, t]);

    return (
        <FormGroup className="interpreted-type">
            <Select
                toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                    <MenuToggle ref={toggleRef} onClick={handleToggleInterpretedType} isFullWidth data-testid="interpreted-type-toggle">
                        <span>{value}&nbsp;</span>
                    </MenuToggle>
                )}

                isOpen={isOpen}
                onOpenChange={() => setIsOpen(!isOpen)}
                popperProps={{ position: 'right' }}
                onSelect={handleSelect}
                shouldFocusFirstItemOnOpen={false}
            >{dropdownItems}</Select>
        </FormGroup>
    );
}

export default memo(InterpretedTypeDropdown);